import { usePage, Link, router } from "@inertiajs/react"
import {
  BarChart3,
  BookOpen,
  BrainCircuit,
  LayoutDashboard,
  LifeBuoy,
  LogOut,
  MessageSquare,
  Plug,
  Settings,
  Ticket,
  type LucideIcon,
} from "lucide-react"
import { LogoMark } from "./logo-mark"
import { CompanySwitcher } from "./company-switcher"
import { navigation, type NavItem, type NavVisibilityCtx } from "@/config/navigation"
import { useCan } from "@/lib/authz"
import { cn } from "@/lib/cn"
import type { SharedProps } from "@/types/inertia"

type TicketsNavQueue = { id: number; name: string; count: number }
type TicketsNav = { total: number; mine: number; queues: TicketsNavQueue[] }

const NAV_ICONS: Record<string, LucideIcon> = {
  dashboard: LayoutDashboard,
  tickets: Ticket,
  "ai-agents": BrainCircuit,
  knowledge: BookOpen,
  escalation: LifeBuoy,
  integrations: Plug,
  feedback: MessageSquare,
  analytics: BarChart3,
  settings: Settings,
}

// Desktop sidebar — pinned to the left edge, full-height, always visible
// at `lg:` and up. Hidden below `lg:` (the mobile-first refactor moved
// the same nav tree into a slide-in drawer triggered by the top-bar
// hamburger). The inner content lives in <SidebarContent> so the same
// tree renders inside the mobile drawer without duplicating logic.
export function Sidebar() {
  return (
    <aside className="thin-scrollbar sticky top-0 hidden h-screen w-72 shrink-0 flex-col justify-between overflow-y-auto border-r border-border bg-app-panel p-4 lg:flex">
      <SidebarContent onNavigate={null} />
    </aside>
  )
}

// Reusable inner sidebar content — header (logo) + nav tree + footer
// (VIP callout, company switcher, sign out). Rendered both by the
// desktop <Sidebar> aside and by the mobile <NavDrawer> (which wraps
// this in a <Sheet>). `onNavigate` is fired whenever the operator
// clicks a nav link; the mobile drawer passes a callback that closes
// itself, the desktop sidebar passes null (no-op).
export function SidebarContent({ onNavigate }: { onNavigate: (() => void) | null }) {
  const page = usePage<SharedProps & { ticketsNav?: TicketsNav }>()
  const { url } = page
  const ticketsNav = page.props.ticketsNav
  const can = useCan()
  const lifecycleState = page.props.current_company?.lifecycle_state
  const visibilityCtx = { lifecycleState }

  // Inject dynamic children under the Tickets group from `ticketsNav`.
  // Empty queue list still leaves All + My as a useful split. Counts =
  // null collapses the parent (no escalated convos at all).
  // Then filter the whole tree by capability — items with a
  // `requiresCapability` the viewer lacks are dropped, and parent groups
  // collapse only when every one of their children also collapsed.
  const items: NavItem[] = navigation
    .map((item) => {
      if (item.label !== "Tickets" || !ticketsNav) return item
      const children: NavItem[] = [
        { label: "All", href: "/tickets", count: ticketsNav.total },
        { label: "My",  href: "/tickets?queue=my", count: ticketsNav.mine },
        ...ticketsNav.queues.map((q) => ({
          label: q.name,
          href:  `/tickets?queue=${q.id}`,
          count: q.count,
        })),
      ]
      return { ...item, children }
    })
    .map((item) => filterByCapability(item, can, visibilityCtx))
    .filter((i): i is NavItem => i !== null)

  return (
    <>
      <div>
        <header className="mb-8 flex items-center gap-3">
          <LogoMark small />
          <div>
            <div className="text-lg font-semibold">
              Valet<span className="text-brand-red">.red</span>
            </div>
            <div className="text-[10px] uppercase tracking-[.22em] text-app-muted">
              Support OS
            </div>
          </div>
        </header>

        <nav className="space-y-1 text-base" onClick={onNavigate ?? undefined}>
          {items.map((item) => (
            <NavRow key={item.label} item={item} currentUrl={url} depth={0} />
          ))}
        </nav>
      </div>

      <footer className="space-y-3 border-t border-border pt-4">
        <div className="rounded-control border border-vip-gold/20 bg-vip-goldMute p-3 text-xs text-vip-goldSoft">
          Every user is a VIP
        </div>
        <CompanySwitcher />
        <button
          type="button"
          onClick={() => router.delete("/users/sign_out")}
          className="flex min-h-11 w-full items-center justify-center gap-2 rounded-control border border-border bg-white/[0.03] px-3 py-2 text-sm text-app-muted transition hover:bg-white/10 hover:text-app-text"
        >
          <LogOut size={14} strokeWidth={2} /> Sign out
        </button>
      </footer>
    </>
  )
}

type RowProps = { item: NavItem; currentUrl: string; depth: number }

function NavRow({ item, currentUrl, depth }: RowProps) {
  const active = item.href ? matchActive(currentUrl, item.href) : false
  const hasChildren = !!item.children?.length
  const padding = depth === 0 ? "" : "px-3 py-2"
  const topRowGrid = "grid min-h-11 grid-cols-[1.25rem_minmax(0,1fr)_1.5rem] items-center gap-2 px-3 py-2"

  // Top-level group with children gets the red-tinted container treatment.
  // When the group is inactive (operator isn't browsing inside it), its
  // children collapse to save vertical space and re-expand on hover —
  // clicking the top-level row still navigates because it's a real Link.
  if (depth === 0 && hasChildren) {
    const groupActive = active || item.children!.some((c) => matchActive(currentUrl, c.href ?? ""))
    return (
      <div className={`group mt-3 rounded-control border ${groupActive ? "border-brand-red/10 bg-brand-red/5" : "border-transparent"}`}>
        <Link
          href={item.href ?? "#"}
          className={`${topRowGrid} rounded-control text-app-text`}
        >
          <NavIcon name={item.icon} active={groupActive} />
          <span className="truncate leading-6">{item.label}</span>
          {hasChildren && <span className={`justify-self-end text-vip-gold text-2xl leading-none transition-transform ${groupActive ? "" : "group-hover:rotate-0 -rotate-90"}`}>▾</span>}
        </Link>
        {/* Hover-intent: 200ms forgiveness before the submenu collapses
            again. Eliminates the "open then close while hovering an
            option" flicker David hit. Using a wrapper that's always
            in-flow (no hidden) plus animated max-height gives a smooth
            collapse without re-flowing the page. */}
        <div className={cn(
          "grid overflow-hidden transition-[grid-template-rows,opacity] duration-200 ease-out",
          groupActive ? "grid-rows-[1fr] opacity-100"
                      : "grid-rows-[0fr] opacity-0 group-hover:grid-rows-[1fr] group-hover:opacity-100 delay-100",
        )}>
          <div className="min-h-0 px-2 pb-2">
            <div className="ml-4 mt-1 space-y-1 border-l border-border pl-3">
              {item.children!.map((child) => (
                <NavRow key={child.label} item={child} currentUrl={currentUrl} depth={depth + 1} />
              ))}
            </div>
          </div>
        </div>
      </div>
    )
  }

  // Leaf or nested item. Top-level rows stay at full text color regardless of
  // active state — the chevron carries the "has children" / collapsed signal.
  // Nested rows still dim when inactive so the active child is obvious.
  const inactiveText = depth === 0 ? "text-app-text" : "text-app-muted"
  const className = active
    ? `rounded-control bg-white/10 ${padding} text-app-text`
    : `rounded-control hover:bg-white/5 ${padding} ${inactiveText}`

  if (item.href) {
    if (depth === 0) {
      return (
        <Link href={item.href} className={`${className} ${topRowGrid}`}>
          <NavIcon name={item.icon} active={active} />
          <span className="truncate leading-6">{item.label}</span>
          <span className="flex shrink-0 items-center justify-end">
            {typeof item.count === "number" && item.count > 0 && (
              <span className={
                active
                  ? "rounded-chip border border-brand-red/40 bg-brand-red/10 px-1.5 py-0.5 text-[10px] font-medium text-brand-red"
                  : "rounded-chip border border-border bg-white/5 px-1.5 py-0.5 text-[10px] font-medium text-app-muted"
              }>
                {item.count}
              </span>
            )}
          </span>
        </Link>
      )
    }

    return (
      <Link href={item.href} className={`${className} flex items-center justify-between gap-2`}>
        <span className="flex min-w-0 items-center gap-2">
          <span className="truncate">{item.label}</span>
        </span>
        <span className="flex shrink-0 items-center gap-2">
          {typeof item.count === "number" && item.count > 0 && (
            <span className={
              active
                ? "rounded-chip border border-brand-red/40 bg-brand-red/10 px-1.5 py-0.5 text-[10px] font-medium text-brand-red"
                : "rounded-chip border border-border bg-white/5 px-1.5 py-0.5 text-[10px] font-medium text-app-muted"
            }>
              {item.count}
            </span>
          )}
          {hasChildren && <span className="text-2xl leading-none">▾</span>}
        </span>
      </Link>
    )
  }

  return <div className={className}>{item.label}</div>
}

function NavIcon({ name, active }: { name?: string; active: boolean }) {
  if (!name) return <span aria-hidden="true" />
  const Icon = NAV_ICONS[name]
  if (!Icon) return <span aria-hidden="true" />

  return (
    <span className="grid h-5 w-5 shrink-0 place-items-center" aria-hidden="true">
      <Icon
        size={16}
        strokeWidth={1.8}
        className={cn(active ? "text-brand-red" : "text-app-muted")}
      />
    </span>
  )
}

// Walks the nav tree, dropping any node whose `requiresCapability` the
// viewer lacks. A parent that DOES pass its own gate but loses every
// child to filtering is also dropped — renders as an empty group
// otherwise. A parent without a `requiresCapability` survives as long as
// it has at least one surviving child OR a usable href; otherwise it
// drops too.
function filterByCapability(item: NavItem, can: (c: string) => boolean, ctx: NavVisibilityCtx): NavItem | null {
  if (item.requiresCapability && !can(item.requiresCapability)) return null
  if (item.visible && !item.visible(ctx)) return null

  const hasChildrenInput = !!item.children?.length
  if (!hasChildrenInput) return item

  const filteredChildren = item.children!
    .map((c) => filterByCapability(c, can, ctx))
    .filter((c): c is NavItem => c !== null)

  if (filteredChildren.length === 0) {
    // Parent had children defined; if all got filtered out, drop the
    // parent — empty groups are noise. The href on the parent is
    // ignored at this point because the whole reason to keep a parent
    // is the children.
    return null
  }
  return { ...item, children: filteredChildren }
}

// Active match: exact path, or path with matching query string.
function matchActive(currentUrl: string, target: string): boolean {
  if (!target || target === "#") return false
  // Strip query for path comparison; if target has a query, require full match.
  const [targetPath, targetQuery] = target.split("?")
  const [currentPath, currentQuery] = currentUrl.split("?")
  if (targetQuery) return target === currentUrl
  return currentPath === targetPath && !currentQuery
}
